import { formatRupiah } from "@/lib/display-format";
import {useSimpleProfit} from './simple-profit-context';
import {simpleTreatmentLabel} from '@/lib/simple-profit-loss';
import { useFinancialReporting } from './financial-reporting-context';
import { applyDescriptionCategoryRules, type DescriptionRule } from "@/lib/description-rules";
import { useEffect, useMemo, useRef, useState } from "react";
import { Clock3, FilePenLine, ShieldCheck, Trash2, WalletCards } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { transactionBrandName, activeBusinessUnits, businessUnitName, type BusinessUnit, type RecipientBrandMapping } from "@/lib/business-units";
import { BrandInput, CategoryInput, SubcategoryInput, type CreateCategory } from "./classification-inputs";
import { CodeMemorySuggestions } from "./transaction-editor";
import type { Transaction } from "@/lib/finance";
import { categoriesForDirection, categoryName, type FinanceCategory } from "@/lib/categories";
import { parseAmount, type Language } from "@/lib/finance";
import {
  MANUAL_DRAFT_VERSION,
  emptyManualTransactionDraft,
  manualDraftToTransaction,
  readManualDraft,
  validateManualTransactionDraft,
  type ManualTransactionDraft,
} from "@/lib/manual-entry";
import { accountName, type FinancialAccount, type FinancialCodeRule } from "@/lib/profit-loss";
import type { UniqueCodeCategoryRule } from "@/lib/supabase-repository";

export type ManualSubmitResult = "saved" | "review" | "failed";

type ManualTransactionEntryProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  language: Language;
  draftKey: string;
  clearSignal: number;
  busy: boolean;
  codeRules: UniqueCodeCategoryRule[];
  descriptionRules: DescriptionRule[];
  financialRules: FinancialCodeRule[];
  recipientMappings: RecipientBrandMapping[];
  businessUnits: BusinessUnit[];
  financialAccounts: FinancialAccount[];
  categories: FinanceCategory[];
  onCreateCategory: CreateCategory;
  transactions: Transaction[];
  onSubmit: (draft: ManualTransactionDraft) => Promise<ManualSubmitResult>;
};

function money(value: number, language: Language) {
  return formatRupiah(value, language);
}

function hasDraftContent(draft: ManualTransactionDraft) {
  const fresh = emptyManualTransactionDraft();
  return draft.date !== fresh.date
    || draft.time !== ""
    || draft.direction !== fresh.direction
    || [
      draft.amount, draft.paymentRef, draft.ftNumber, draft.subject, draft.internalDescription,
      draft.sourceName, draft.sourceBank, draft.beneficiaryName, draft.beneficiaryBank,
      draft.sourceAccount, draft.beneficiaryAccount, draft.transferType, draft.balance,
      draft.category, draft.businessUnit, draft.financialAccount,
    ].some((value) => value.trim() !== "");
}

export function ManualTransactionEntry({
  open,
  onOpenChange,
  language,
  draftKey,
  clearSignal,
  busy,
  codeRules, descriptionRules,
  financialRules,
  recipientMappings,
  businessUnits,
  financialAccounts,
  categories,
  onCreateCategory,
  transactions,
  onSubmit,
}: ManualTransactionEntryProps) {
  const reporting=useFinancialReporting();
  const [draft, setDraft] = useState<ManualTransactionDraft>(() => emptyManualTransactionDraft());
  const [loaded, setLoaded] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  const [storageFailed, setStorageFailed] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const previousClearSignal = useRef(clearSignal);

  const text = language === "id" ? {
    title: "Input transaksi manual",
    description: "Catat transaksi tanpa file impor. Data tetap melewati aturan kode/deskripsi, brand, akun laporan, dan pemeriksaan duplikasi.",
    restored: "Draft sebelumnya dipulihkan",
    saved: "Draft tersimpan otomatis",
    storageFailed: "Draft tidak dapat disimpan di perangkat ini. Jangan tutup halaman sebelum transaksi berhasil disimpan.",
    primary: "Data utama",
    direction: "Tipe transaksi",
    credit: "Kredit / pemasukan",
    debit: "Debet / pengeluaran",
    amount: "Nominal",
    date: "Tanggal (opsional)",
    time: "Waktu (opsional)",
    reference: "No. referensi (opsional)",
    subject: "Deskripsi transaksi (opsional)",
    internal: "Deskripsi EventKita (opsional)",
    parties: "Pengirim dan penerima",
    sender: "Nama pengirim",
    senderBank: "Bank pengirim",
    recipient: "Nama penerima",
    recipientBank: "Bank penerima",
    classification: "Klasifikasi",
    automatic: "Tentukan otomatis",
    category: "Kategori",
    brand: "Brand / unit bisnis",
    reportAccount: "Akun laporan",
    preview: "Hasil otomatis saat ini",
    code: "Kode",
    advanced: "Data tambahan",
    sourceAccount: "No. rekening sumber",
    recipientAccount: "No. rekening tujuan",
    transferType: "Jenis transfer",
    balance: "Saldo setelah transaksi",
    status: "Status",
    success: "Berhasil",
    failed: "Gagal",
    pending: "Pending",
    clear: "Hapus draft",
    clearQuestion: "Hapus seluruh isi draft ini? Tindakan ini tidak menghapus transaksi yang sudah tersimpan.",
    keep: "Pertahankan",
    confirmClear: "Ya, hapus draft",
    save: "Periksa dan simpan",
    saving: "Menyimpan…",
    saveFailed: "Transaksi belum tersimpan. Draft tetap aman; periksa koneksi dan pastikan migrasi v0.12.0 sudah dijalankan.",
    directionError: "Pilih Debet atau Kredit.",
    amountError: "Nominal harus lebih besar dari nol.",
    draftScope: "Draft ini hanya tersimpan pada perangkat dan akun yang sedang digunakan.",
  } : {
    title: "Manual transaction entry",
    description: "Record a transaction without importing a file. Code, brand, report-account, and duplicate rules still apply.",
    restored: "Previous draft restored",
    saved: "Draft saved automatically",
    storageFailed: "The draft cannot be saved on this device. Keep this page open until the transaction is saved.",
    primary: "Primary details",
    direction: "Transaction type",
    credit: "Credit / income",
    debit: "Debit / expense",
    amount: "Amount",
    date: "Date (optional)",
    time: "Time (optional)",
    reference: "Reference no. (optional)",
    subject: "Transaction description (optional)",
    internal: "EventKita description (optional)",
    parties: "Sender and recipient",
    sender: "Sender name",
    senderBank: "Sender bank",
    recipient: "Recipient name",
    recipientBank: "Recipient bank",
    classification: "Classification",
    automatic: "Determine automatically",
    category: "Category",
    brand: "Brand / business unit",
    reportAccount: "Report account",
    preview: "Current automatic result",
    code: "Code",
    advanced: "Additional data",
    sourceAccount: "Source account no.",
    recipientAccount: "Recipient account no.",
    transferType: "Transfer type",
    balance: "Balance after transaction",
    status: "Status",
    success: "Success",
    failed: "Failed",
    pending: "Pending",
    clear: "Delete draft",
    clearQuestion: "Delete the entire draft? This does not delete a transaction that has already been saved.",
    keep: "Keep draft",
    confirmClear: "Yes, delete draft",
    save: "Review and save",
    saving: "Saving…",
    saveFailed: "The transaction was not saved. Your draft is safe; check the connection and make sure the v0.12.0 migration has been applied.",
    directionError: "Select Debit or Credit.",
    amountError: "Amount must be greater than zero.",
    draftScope: "This draft is stored only on the current device and account.",
  };

  useEffect(() => {
    setLoaded(false);
    setStorageFailed(false);
    try {
      const stored = readManualDraft(localStorage.getItem(draftKey));
      if (stored) {
        setDraft(stored.data);
        setSavedAt(stored.savedAt);
        setRestored(true);
      } else {
        setDraft(emptyManualTransactionDraft());
        setSavedAt(null);
        setRestored(false);
      }
    } catch {
      setDraft(emptyManualTransactionDraft());
      setStorageFailed(true);
    } finally {
      setLoaded(true);
    }
  }, [draftKey]);

  useEffect(() => {
    if (!loaded) return;
    try {
      if (!hasDraftContent(draft)) {
        localStorage.removeItem(draftKey);
        setSavedAt(null);
        return;
      }
      const nextSavedAt = new Date().toISOString();
      localStorage.setItem(draftKey, JSON.stringify({
        version: MANUAL_DRAFT_VERSION,
        savedAt: nextSavedAt,
        data: draft,
      }));
      setSavedAt(nextSavedAt);
      setStorageFailed(false);
    } catch {
      setStorageFailed(true);
    }
  }, [draft, draftKey, loaded]);

  useEffect(() => {
    if (previousClearSignal.current === clearSignal) return;
    previousClearSignal.current = clearSignal;
    try { localStorage.removeItem(draftKey); } catch { /* The saved transaction is authoritative. */ }
    setDraft(emptyManualTransactionDraft());
    setSavedAt(null);
    setRestored(false);
    setShowErrors(false);
    setSubmitFailed(false);
    setConfirmClear(false);
  }, [clearSignal, draftKey]);

  const errors = validateManualTransactionDraft(draft);
  const preview = useMemo(() => {
    try {
      return applyDescriptionCategoryRules([manualDraftToTransaction(draft, { codeRules, financialRules, recipientMappings })], descriptionRules, codeRules, categories, businessUnits)[0];
    } catch {
      return null;
    }
  }, [draft, codeRules, financialRules, recipientMappings, descriptionRules, categories, businessUnits]);

  const categoryOptions = categoriesForDirection(categories, draft.direction);
  const activeUnits = activeBusinessUnits(businessUnits);
  const simpleProfit=useSimpleProfit();
  const effectiveAccount=preview?reporting?.resolve(preview).account:undefined;
  const activeAccounts = financialAccounts.filter((account) => account.active).sort((a, b) => a.displayOrder - b.displayOrder);

  const update = <K extends keyof ManualTransactionDraft>(key: K, value: ManualTransactionDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setSubmitFailed(false);
    setRestored(false);
  };

  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch { setStorageFailed(true); }
    setDraft(emptyManualTransactionDraft());
    setSavedAt(null);
    setRestored(false);
    setShowErrors(false);
    setSubmitFailed(false);
    setConfirmClear(false);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setShowErrors(true);
    setSubmitFailed(false);
    if (Object.keys(errors).length) return;
    const result = await onSubmit(draft);
    if (result === "failed") setSubmitFailed(true);
  };

  const savedLabel = savedAt
    ? new Intl.DateTimeFormat(language === "id" ? "id-ID" : "en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(savedAt))
    : "";

  return <Sheet open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
      <SheetHeader className="border-b px-4 py-5 sm:px-6">
        <SheetTitle className="flex items-center gap-2"><FilePenLine className="size-5" />{text.title}</SheetTitle>
        <SheetDescription>{text.description}</SheetDescription>
      </SheetHeader>
      <form id="manual-transaction-form" className="space-y-5 px-4 pb-2 sm:px-6" onSubmit={submit}>
        <div className={`rounded border p-3 text-xs ${storageFailed ? "border-red-500/35 bg-red-500/8 text-red-700 dark:text-red-300" : "border-emerald-500/30 bg-emerald-500/8 text-emerald-700 dark:text-emerald-300"}`}>
          <p className="flex items-center gap-2 font-semibold"><Clock3 className="size-4" />{storageFailed ? text.storageFailed : restored ? text.restored : `${text.saved}${savedLabel ? ` · ${savedLabel}` : ""}`}</p>
          {!storageFailed && <p className="mt-1 text-muted-foreground">{text.draftScope}</p>}
        </div>

        {submitFailed && <div role="alert" className="rounded border border-red-500/35 bg-red-500/8 p-3 text-sm text-red-700 dark:text-red-300">{text.saveFailed}</div>}

        <FormSection title={text.primary}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={text.direction} error={showErrors && errors.direction ? text.directionError : ""}>
              <Select value={draft.direction || "empty"} onValueChange={(value) => {
                setDraft((current) => ({ ...current, direction: value as ManualTransactionDraft["direction"], category: "", subcategory: "", financialAccount: "" }));
                setSubmitFailed(false);
                setRestored(false);
              }}>
                <SelectTrigger className="min-h-11 w-full"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="empty" disabled>{language === "id" ? "Pilih tipe" : "Select type"}</SelectItem><SelectItem value="CREDIT">{text.credit}</SelectItem><SelectItem value="DEBIT">{text.debit}</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label={text.amount} error={showErrors && errors.amount ? text.amountError : ""}>
              <Input inputMode="decimal" value={draft.amount} onChange={(event) => update("amount", event.target.value)} placeholder="Rp0" className="min-h-11 text-base font-semibold tabular-nums" aria-invalid={showErrors && Boolean(errors.amount)} />
            </Field>
            <Field label={text.date}>
              <Input type="date" value={draft.date} onChange={(event) => update("date", event.target.value)} className="min-h-11" />
            </Field>
            <Field label={text.time}><Input type="time" value={draft.time} onChange={(event) => update("time", event.target.value)} className="min-h-11" /></Field>
          </div>
          <Field label={text.reference}><Input value={draft.paymentRef} onChange={(event) => update("paymentRef", event.target.value)} placeholder="Contoh: BIFAST-123456" className="min-h-11 font-mono" /></Field>
          <Field label="FT Number"><Input value={draft.ftNumber} onChange={(event) => update("ftNumber", event.target.value)} className="min-h-11 font-mono" /></Field>
          <Field label={text.subject}>
            <textarea value={draft.subject} onChange={(event) => update("subject", event.target.value)} rows={3} maxLength={500} placeholder={language === "id" ? "Deskripsi asli transaksi" : "Original transaction description"} className="min-h-24 w-full resize-y rounded border bg-background px-3 py-2 text-sm outline-none ring-ring/40 transition focus:ring-2" />
          </Field>
          <Field label={text.internal}><textarea value={draft.internalDescription} onChange={(event) => update("internalDescription", event.target.value)} rows={2} maxLength={240} placeholder={language === "id" ? "Catatan internal transaksi" : "Internal transaction note"} className="min-h-20 w-full resize-y rounded border bg-background px-3 py-2 text-sm outline-none ring-ring/40 transition focus:ring-2" /></Field>
        </FormSection>

        <FormSection title={text.parties}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={text.sender}><Input value={draft.sourceName} onChange={(event) => update("sourceName", event.target.value)} className="min-h-11" /></Field>
            <Field label={text.senderBank}><Input value={draft.sourceBank} onChange={(event) => update("sourceBank", event.target.value)} className="min-h-11" /></Field>
            <Field label={text.recipient}><Input value={draft.beneficiaryName} onChange={(event) => update("beneficiaryName", event.target.value)} className="min-h-11" /></Field>
            <Field label={text.recipientBank}><Input value={draft.beneficiaryBank} onChange={(event) => update("beneficiaryBank", event.target.value)} className="min-h-11" /></Field>
          </div>
        </FormSection>

        <FormSection title={text.classification}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={language === "id" ? "Subkategori (opsional)" : "Subcategory (optional)"}><SubcategoryInput value={draft.subcategory} parent={draft.category} categories={categories} language={language} onChange={(value) => update("subcategory", value || "")} /></Field>
            <Field label={text.category}><CategoryInput value={draft.category} automatic direction={draft.direction} categories={categories} language={language} onChange={(value) => { update("category", value); update("subcategory", ""); }} onCreate={onCreateCategory} /></Field>
            <Field label={text.brand}><BrandInput value={draft.businessUnit} units={businessUnits} language={language} candidates={preview?.brandCandidates} onChange={(value) => update("businessUnit", value)} /><Button size="sm" variant="ghost" onClick={() => update("businessUnit", "")}>{text.automatic}</Button></Field>
            <details className="rounded border p-3"><summary className="cursor-pointer text-sm font-semibold">{language==="id"?"Akun laporan (opsional / lanjutan)":"Report account (optional / advanced)"}</summary><Field label={text.reportAccount}><Select value={draft.financialAccount || "auto"} onValueChange={(value) => update("financialAccount", value === "auto" ? "" : value)}><SelectTrigger className="min-h-11 w-full"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="auto">{text.automatic}</SelectItem>{activeAccounts.map((account) => <SelectItem key={account.slug} value={account.slug}>{accountName(account, language)}</SelectItem>)}</SelectContent></Select></Field></details>
          </div>
          <div className="rounded border bg-muted/25 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{text.preview}</p>
            {preview ? <div className="mt-3 grid gap-3 sm:grid-cols-4"><PreviewValue label={text.code} value={preview.uniqueCode} mono /><PreviewValue label={text.category} value={categoryName(preview.category, language, categories) + (preview.subcategory ? " / " + categoryName(preview.subcategory, language, categories) : "")} /><PreviewValue label={text.brand} value={transactionBrandName(preview, language, businessUnits)} /><PreviewValue label={language==="id"?"Laba/rugi otomatis":"Automatic profit/loss"} value={simpleProfit?simpleTreatmentLabel(simpleProfit.resolve(preview).treatment,language):effectiveAccount?accountName(effectiveAccount,language):"—"} /></div> : <p className="mt-3 text-sm text-muted-foreground">{language === "id" ? "Lengkapi nominal dan tipe transaksi untuk melihat hasil." : "Complete the amount and transaction type to see the result."}</p>}
          </div>
          {preview?.categoryRuleConflict && <p role="alert" className="text-sm text-amber-700">{language === "id" ? "Konflik aturan: tentukan kategori manual atau ubah prioritas aturan." : "Rule conflict: choose manually or change priorities."}</p>}
          {preview && <CodeMemorySuggestions transaction={preview} history={transactions} rules={codeRules} mappings={recipientMappings} units={businessUnits} categories={categories} language={language} onChoose={(value, context) => { setDraft((current) => ({ ...current, category: value, subcategory: "", businessUnit: context !== "unassigned" ? context : current.businessUnit })); }} />}
        </FormSection>

        <details className="rounded border bg-card">
          <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold">{text.advanced}</summary>
          <div className="grid gap-4 border-t p-4 sm:grid-cols-2">
            <Field label={text.sourceAccount}><Input value={draft.sourceAccount} onChange={(event) => update("sourceAccount", event.target.value)} inputMode="numeric" className="min-h-11" /></Field>
            <Field label={text.recipientAccount}><Input value={draft.beneficiaryAccount} onChange={(event) => update("beneficiaryAccount", event.target.value)} inputMode="numeric" className="min-h-11" /></Field>
            <Field label={text.transferType}><Input value={draft.transferType} onChange={(event) => update("transferType", event.target.value)} className="min-h-11" /></Field>
            <Field label={text.balance}><Input value={draft.balance} onChange={(event) => update("balance", event.target.value)} inputMode="decimal" className="min-h-11" /></Field>
          </div>
        </details>

        {preview && <div className="flex items-center justify-between gap-3 rounded border border-[#a13b6e]/30 bg-[#f5e5ed] p-4 dark:bg-[#3a2431]"><span className="flex items-center gap-2 text-sm font-medium"><WalletCards className="size-4 text-[#a13b6e]" />{draft.direction === "CREDIT" ? text.credit : text.debit}</span><span className="font-bold tabular-nums text-[#82305b] dark:text-[#f1b9d5]">{money(parseAmount(draft.amount), language)}</span></div>}
      </form>

      <SheetFooter className="sticky bottom-0 border-t bg-background/95 px-4 py-4 backdrop-blur sm:px-6">
        {confirmClear && <div className="mb-2 rounded border border-red-500/35 bg-red-500/8 p-3 text-sm"><p>{text.clearQuestion}</p><div className="mt-3 grid grid-cols-2 gap-2"><Button type="button" variant="outline" onClick={() => setConfirmClear(false)}>{text.keep}</Button><Button type="button" variant="destructive" onClick={clearDraft}><Trash2 />{text.confirmClear}</Button></div></div>}
        <div className="grid grid-cols-[auto_1fr] gap-2">
          <Button type="button" variant="outline" disabled={busy || !hasDraftContent(draft)} onClick={() => setConfirmClear(true)} aria-label={text.clear}><Trash2 /><span className="hidden sm:inline">{text.clear}</span></Button>
          <Button type="submit" form="manual-transaction-form" disabled={busy}><ShieldCheck />{busy ? text.saving : text.save}</Button>
        </div>
      </SheetFooter>
    </SheetContent>
  </Sheet>;
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="space-y-4 rounded border bg-card p-4 sm:p-5"><h3 className="text-sm font-bold uppercase tracking-[0.08em] text-[#82305b] dark:text-[#f1b9d5]">{title}</h3>{children}</section>;
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return <label className="block min-w-0"><span className="mb-1.5 block text-xs font-semibold text-muted-foreground">{label}</span>{children}{error && <span className="mt-1.5 block text-xs text-red-600 dark:text-red-300">{error}</span>}</label>;
}

function PreviewValue({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className={`mt-1 truncate text-sm font-semibold ${mono ? "font-mono" : ""}`} title={value}>{value}</p></div>;
}
